import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { resendProfileEmailVerification } from '../services/authService';
import RatingDisplay from './RatingDisplay';
import { useToast } from '../context/ToastContext';
import styles from '../styles/Profile.module.css';
import layoutStyles from '../styles/modules/PageLayout.module.css';
import headerStyles from '../styles/modules/Header.module.css';
import cardStyles from '../styles/modules/Card.module.css';
import formStyles from '../styles/modules/Form.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import backgroundImage from '../images/background.jpg';

interface Experience {
    id: string;
    position: string;
    company: string;
    startDate: string;
    endDate: string;
    description: string;
}

interface APIExperience {
    position: string;
    company: string;
    startMonth: string;
    startYear: number;
    endMonth?: string;
    endYear?: number;
    description?: string;
}

interface User {
    _id?: string;
    username: string;
    fullName: string;
    businessName?: string;
    email: string;
    role: string;
    phone?: string;
    userType?: string;
    profilePicture?: string;
    emailVerified?: boolean;
    preferredLanguage?: string;
    placeOfResidence?: string;
    experience?: APIExperience[];
    experiences?: Experience[];
}

const Profile: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { showToast } = useToast();
    const [user, setUser] = useState<User | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const [isAddingExperience, setIsAddingExperience] = useState(false);
    const [editingExpId, setEditingExpId] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [activeTab, setActiveTab] = useState<'profile' | 'experience' | 'ratings'>('profile');

    // Staff roles only
    const staffRoles = [
        'Barman',
        'Bartender',
        'Waiter',
        'Waitress',
        'Chef',
        'Sous Chef',
        'Cook',
        'Dishwasher',
        'Host/Hostess',
        'Sommelier',
        'Busser',
        'Kitchen Porter',
        'Line Cook',
        'Pastry Chef'
    ];

    // Employer roles only
    const employerRoles = [
        'Restaurant Owner',
        'Restaurant Manager',
        'General Manager',
        'Bar Manager',
        'Kitchen Manager',
        'Operations Manager',
        'F&B Manager'
    ];

    const [formData, setFormData] = useState({
        fullName: '',
        businessName: '',
        email: '',
        phone: '',
        role: '',
        placeOfResidence: ''
    });
    const [expFormData, setExpFormData] = useState({
        position: '',
        company: '',
        startDate: '',
        endDate: '',
        description: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem('authToken');

            if (!token) {
                navigate('/');
                return;
            }

            try {
                // Fetch profile from API
                const response = await fetch(getApiUrl('users/profile'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        // Token is invalid, redirect to login
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('user');
                        navigate('/');
                        return;
                    }
                    throw new Error('Failed to fetch profile');
                }

                const profileData = await response.json();
                console.log('Profile data from API:', profileData);

                // Set language from user preference
                if (profileData.preferredLanguage) {
                    i18n.changeLanguage(profileData.preferredLanguage);
                }

                // Update user state
                setUser(profileData);
                setFormData({
                    fullName: profileData.fullName || '',
                    businessName: profileData.businessName || '',
                    email: profileData.email || '',
                    phone: profileData.phone || '',
                    role: profileData.role || '',
                    placeOfResidence: profileData.placeOfResidence || ''
                });

                // Convert API experience format to component format
                if (profileData.experience && Array.isArray(profileData.experience)) {
                    const convertedExperiences = profileData.experience.map((exp: any, index: number) => ({
                        id: index.toString(),
                        position: exp.position,
                        company: exp.company,
                        startDate: `${exp.startYear}-${getMonthNumber(exp.startMonth)}`,
                        endDate: exp.endYear ? `${exp.endYear}-${getMonthNumber(exp.endMonth)}` : '',
                        description: exp.description || ''
                    }));
                    setExperiences(convertedExperiences);
                }

                // Update localStorage with fresh data
                localStorage.setItem('user', JSON.stringify(profileData));
            } catch (error) {
                console.error('Error fetching profile:', error);
                // Fallback to localStorage if API fails
                const userData = localStorage.getItem('user');
                if (userData) {
                    try {
                        const parsedUser = JSON.parse(userData);
                        setUser(parsedUser);
                        setFormData({
                            fullName: parsedUser.fullName || '',
                            businessName: parsedUser.businessName || '',
                            email: parsedUser.email || '',
                            phone: parsedUser.phone || '',
                            role: parsedUser.role || '',
                            placeOfResidence: parsedUser.placeOfResidence || ''
                        });
                        setExperiences(parsedUser.experiences || []);
                    } catch {
                        navigate('/');
                    }
                } else {
                    navigate('/');
                }
            }
        };

        fetchProfile();
    }, [navigate, i18n]);

    // Helper function to convert month name to number
    const getMonthNumber = (monthName: string): string => {
        const months: { [key: string]: string } = {
            'January': '01', 'February': '02', 'March': '03', 'April': '04',
            'May': '05', 'June': '06', 'July': '07', 'August': '08',
            'September': '09', 'October': '10', 'November': '11', 'December': '12'
        };
        return months[monthName] || '01';
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        const token = localStorage.getItem('authToken');
        
        if (!token) {
            console.error('No auth token found');
            return;
        }

        try {
            // Prepare update data - only include fields that are relevant
            const updateData: any = {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                role: formData.role
            };

            if (user?.userType === 'employer') {
                updateData.businessName = formData.businessName;
            }

            // Include staff-specific fields only if user is staff
            if (user?.userType === 'staff') {
                if (formData.placeOfResidence) {
                    updateData.placeOfResidence = formData.placeOfResidence;
                }
            }

            // Call API to update profile
            const response = await fetch(getApiUrl('users/profile'), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updateData)
            });

            if (!response.ok) {
                throw new Error('Failed to update profile');
            }

            const updatedProfile = await response.json();
            console.log('Profile updated:', updatedProfile);

            // Update local storage and state
            localStorage.setItem('user', JSON.stringify(updatedProfile));
            setUser(updatedProfile);
            setFormData({
                fullName: updatedProfile.fullName || '',
                businessName: updatedProfile.businessName || '',
                email: updatedProfile.email || '',
                phone: updatedProfile.phone || '',
                role: updatedProfile.role || '',
                placeOfResidence: updatedProfile.placeOfResidence || ''
            });

            setIsEditing(false);
            showToast({ message: t('messages.updateSuccess'), type: 'success' });
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast({ message: t('messages.errorOccurred'), type: 'error' });
        }
    };

    const handleCancel = () => {
        if (user) {
            setFormData({
                fullName: user.fullName || '',
                businessName: user.businessName || '',
                email: user.email || '',
                phone: user.phone || '',
                role: user.role || '',
                placeOfResidence: user.placeOfResidence || ''
            });
        }
        setIsEditing(false);
    };

    const handleResendVerification = async () => {
        const token = localStorage.getItem('authToken');

        if (!token) {
            showToast({ message: t('messages.loginRequired'), type: 'error' });
            return;
        }

        setResendingVerification(true);

        try {
            const response = await resendProfileEmailVerification(token);
            showToast({ message: response.message || t('profile.verificationEmailSent'), type: 'success' });
        } catch (error) {
            if (error instanceof Error) {
                showToast({ message: error.message, type: 'error' });
            } else {
                showToast({ message: t('messages.errorOccurred'), type: 'error' });
            }
        } finally {
            setResendingVerification(false);
        }
    };

    const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLanguage = e.target.value;
        const token = localStorage.getItem('authToken');
        
        if (!token) return;

        try {
            // Update language in API
            const response = await fetch(getApiUrl('users/profile'), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ preferredLanguage: newLanguage })
            });

            if (!response.ok) {
                throw new Error('Failed to update language preference');
            }

            // Change language in i18n
            i18n.changeLanguage(newLanguage);

            // Update user state
            if (user) {
                const updatedUser = { ...user, preferredLanguage: newLanguage };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
        } catch (error) {
            console.error('Error updating language:', error);
            showToast({ message: t('messages.errorOccurred'), type: 'error' });
        }
    };

    const uploadProfilePicture = async (file: File) => {
        const token = localStorage.getItem('authToken');
        
        if (!token) {
            throw new Error('Not authenticated');
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(getApiUrl('users/profile/picture'), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error('Upload failed');
        }

        const data = await response.json();
        return data.profilePicture;
    };

    const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        
        if (!file) return;

        // Validate file type
        if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
            showToast({ message: t('messages.invalidFileType'), type: 'error' });
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast({ message: t('messages.fileTooLarge'), type: 'error' });
            return;
        }

        try {
            setUploading(true);
            const newImageUrl = await uploadProfilePicture(file);
            
            // Update user state with new profile picture
            if (user) {
                const updatedUser = { ...user, profilePicture: newImageUrl };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
            
            showToast({ message: t('messages.uploadSuccess'), type: 'success' });
        } catch (error) {
            console.error('Upload error:', error);
            showToast({ message: t('messages.uploadFailed'), type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleExpInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setExpFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddExperience = () => {
        setIsAddingExperience(true);
        setEditingExpId(null);
        setExpFormData({
            position: '',
            company: '',
            startDate: '',
            endDate: '',
            description: ''
        });
    };

    const handleEditExperience = (exp: Experience) => {
        setIsAddingExperience(true);
        setEditingExpId(exp.id);
        setExpFormData({
            position: exp.position,
            company: exp.company,
            startDate: exp.startDate,
            endDate: exp.endDate,
            description: exp.description
        });
    };

    const handleSaveExperience = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            console.error('No auth token found');
            return;
        }

        try {
            if (editingExpId) {
                // Update existing experience using PATCH /users/experience/:index
                const index = experiences.findIndex(exp => exp.id === editingExpId);
                
                // Convert date format from YYYY-MM to month name and year
                const [startYear, startMonth] = expFormData.startDate.split('-');
                const [endYear, endMonth] = expFormData.endDate ? expFormData.endDate.split('-') : ['', ''];
                
                const updateData: any = {
                    position: expFormData.position,
                    company: expFormData.company,
                    startMonth: getMonthName(startMonth),
                    startYear: parseInt(startYear),
                    description: expFormData.description
                };

                if (endYear && endMonth) {
                    updateData.endMonth = getMonthName(endMonth);
                    updateData.endYear = parseInt(endYear);
                }

                const response = await fetch(getApiUrl(`users/experience/${index}`), {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(updateData)
                });

                if (!response.ok) {
                    throw new Error('Failed to update experience');
                }

                const updatedProfile = await response.json();
                console.log('Experience updated:', updatedProfile);
                
                // Update state with new data from API
                if (updatedProfile.experience && Array.isArray(updatedProfile.experience)) {
                    const convertedExperiences = updatedProfile.experience.map((exp: any, idx: number) => ({
                        id: idx.toString(),
                        position: exp.position,
                        company: exp.company,
                        startDate: `${exp.startYear}-${getMonthNumber(exp.startMonth)}`,
                        endDate: exp.endYear ? `${exp.endYear}-${getMonthNumber(exp.endMonth)}` : '',
                        description: exp.description || ''
                    }));
                    setExperiences(convertedExperiences);
                }
                
            } else {
                // Add new experience using POST /users/experience
                const [startYear, startMonth] = expFormData.startDate.split('-');
                const [endYear, endMonth] = expFormData.endDate ? expFormData.endDate.split('-') : ['', ''];
                
                const experienceData: any = {
                    position: expFormData.position,
                    company: expFormData.company,
                    startMonth: getMonthName(startMonth),
                    startYear: parseInt(startYear),
                    description: expFormData.description
                };

                if (endYear && endMonth) {
                    experienceData.endMonth = getMonthName(endMonth);
                    experienceData.endYear = parseInt(endYear);
                }

                console.log('Adding experience:', experienceData);

                const response = await fetch(getApiUrl('users/experience'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(experienceData)
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('API Error:', errorData);
                    throw new Error('Failed to add experience');
                }

                const updatedProfile = await response.json();
                console.log('Experience added:', updatedProfile);
                
                // Update state with new data from API
                if (updatedProfile.experience && Array.isArray(updatedProfile.experience)) {
                    const convertedExperiences = updatedProfile.experience.map((exp: any, idx: number) => ({
                        id: idx.toString(),
                        position: exp.position,
                        company: exp.company,
                        startDate: `${exp.startYear}-${getMonthNumber(exp.startMonth)}`,
                        endDate: exp.endYear ? `${exp.endYear}-${getMonthNumber(exp.endMonth)}` : '',
                        description: exp.description || ''
                    }));
                    setExperiences(convertedExperiences);
                }
            }
            
            setIsAddingExperience(false);
            setEditingExpId(null);
        } catch (error) {
            console.error('Error saving experience:', error);
            showToast({ message: t('messages.errorOccurred'), type: 'error' });
        }
    };

    // Helper function to convert month number to name
    const getMonthName = (monthNumber: string): string => {
        const months: { [key: string]: string } = {
            '01': 'January', '02': 'February', '03': 'March', '04': 'April',
            '05': 'May', '06': 'June', '07': 'July', '08': 'August',
            '09': 'September', '10': 'October', '11': 'November', '12': 'December'
        };
        return months[monthNumber] || 'January';
    };

    const handleDeleteExperience = async (id: string) => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            console.error('No auth token found');
            return;
        }

        const index = experiences.findIndex(exp => exp.id === id);
        if (index === -1) {
            console.error('Experience not found');
            return;
        }

        try {
            const response = await fetch(getApiUrl(`users/experience/${index}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete experience');
            }

            const updatedProfile = await response.json();
            console.log('Experience deleted:', updatedProfile);

            // Update state with new data from API
            if (updatedProfile.experience && Array.isArray(updatedProfile.experience)) {
                const convertedExperiences = updatedProfile.experience.map((exp: any, idx: number) => ({
                    id: idx.toString(),
                    position: exp.position,
                    company: exp.company,
                    startDate: `${exp.startYear}-${getMonthNumber(exp.startMonth)}`,
                    endDate: exp.endYear ? `${exp.endYear}-${getMonthNumber(exp.endMonth)}` : '',
                    description: exp.description || ''
                }));
                setExperiences(convertedExperiences);
            } else {
                setExperiences([]);
            }
        } catch (error) {
            console.error('Error deleting experience:', error);
            showToast({ message: t('messages.errorOccurred'), type: 'error' });
        }
    };

    const handleCancelExperience = () => {
        setIsAddingExperience(false);
        setEditingExpId(null);
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    if (!user) {
        return null;
    }

    return (
        <div className={layoutStyles.page}>
            <header className={headerStyles.header}>
                <button className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`} onClick={handleBackClick} title={t('common.backToDashboard')} aria-label={t('common.backToDashboard')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={headerStyles.title}>{t('profile.title')}</h1>
                <div></div>
            </header>

            <div className={layoutStyles.container} style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className={layoutStyles.content}>
                    <div className={cardStyles.cardGlass}>
                        <div className={styles.profileAvatarContainer}>
                            <div className={styles.profileAvatar}>
                                {user.profilePicture ? (
                                    <img 
                                        src={user.profilePicture} 
                                        alt="Profile" 
                                        className={styles.profilePicture}
                                    />
                                ) : (
                                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                    </svg>
                                )}
                            </div>
                            <div className={styles.profilePictureUpload}>
                                <label htmlFor="profile-picture-input" className={buttonStyles.upload}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="17 8 12 3 7 8"/>
                                        <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    {uploading ? t('profile.uploading') : t('profile.changePhoto')}
                                </label>
                                <input
                                    id="profile-picture-input"
                                    type="file"
                                    accept="image/jpeg,image/png,image/jpg,image/webp"
                                    onChange={handleProfilePictureChange}
                                    disabled={uploading}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className={styles.profileTabs}>
                            <button 
                                className={`${buttonStyles.tab} ${activeTab === 'profile' ? buttonStyles.active : ''}`}
                                onClick={() => setActiveTab('profile')}
                            >
                                {t('profile.profileTab') || 'Profile'}
                            </button>
                            {user.userType !== 'employer' && (
                                <button 
                                    className={`${buttonStyles.tab} ${activeTab === 'experience' ? buttonStyles.active : ''}`}
                                    onClick={() => setActiveTab('experience')}
                                >
                                    {t('profile.experienceTab') || 'Experience'}
                                </button>
                            )}
                            <button 
                                className={`${buttonStyles.tab} ${activeTab === 'ratings' ? buttonStyles.active : ''}`}
                                onClick={() => setActiveTab('ratings')}
                            >
                                {t('profile.ratingsTab') || 'Ratings'}
                            </button>
                        </div>

                        {/* Profile Tab Content */}
                        {activeTab === 'profile' && (
                        <>
                        {!user.emailVerified && (
                            <div className={styles.emailVerificationBanner}>
                                <div className={styles.emailVerificationBannerText}>
                                    <strong>{t('profile.emailNotVerified')}</strong>
                                </div>
                                <button
                                    type="button"
                                    className={styles.verifyEmailButton}
                                    onClick={handleResendVerification}
                                    disabled={resendingVerification}
                                >
                                    {resendingVerification ? t('common.loading') : t('profile.verifyEmail')}
                                </button>
                            </div>
                        )}

                        <div className={styles.profileForm}>
                            {user.userType === 'employer' && (
                                <div className={formStyles.formGroup}>
                                    <label htmlFor="businessName" className={formStyles.labelLight}>{t('profile.businessName')}</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            id="businessName"
                                            name="businessName"
                                            value={formData.businessName}
                                            onChange={handleInputChange}
                                            className={formStyles.inputDark}
                                            placeholder={t('profile.enterBusinessName')}
                                        />
                                    ) : (
                                        <div className={formStyles.value}>{formData.businessName || t('profile.notProvided')}</div>
                                    )}
                                </div>
                            )}

                            <div className={formStyles.formGroup}>
                                <label htmlFor="fullName" className={formStyles.labelLight}>{t('profile.fullName')}</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className={formStyles.inputDark}
                                    />
                                ) : (
                                    <div className={formStyles.value}>{user.fullName}</div>
                                )}
                            </div>

                            <div className={formStyles.formGroup}>
                                <label htmlFor="email" className={formStyles.labelLight}>{t('profile.email')}</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={formStyles.inputDark}
                                    />
                                ) : (
                                    <div className={formStyles.value}>{user.email}</div>
                                )}
                            </div>

                        <div className={formStyles.formGroup}>
                            <label htmlFor="role" className={formStyles.labelLight}>{t('profile.role')}</label>
                            {isEditing ? (
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    className={formStyles.inputDark}
                                >
                                    {(user.userType === 'staff' ? staffRoles : employerRoles).map((role) => (
                                        <option key={role} value={role}>
                                            {t(`roles.${role.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className={formStyles.value}>
                                    {t(`roles.${user.role.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                </div>
                            )}
                        </div>                            <div className={formStyles.formGroup}>
                                <label htmlFor="phone" className={formStyles.labelLight}>{t('profile.phone')}</label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className={formStyles.inputDark}
                                        placeholder="+1234567890"
                                    />
                                ) : (
                                    <div className={formStyles.value}>{user.phone || t('profile.notProvided')}</div>
                                )}
                            </div>

                            {/* Staff-only fields */}
                            {user.userType === 'staff' && (
                                <div className={formStyles.formGroup}>
                                    <label htmlFor="placeOfResidence" className={formStyles.labelLight}>{t('profile.placeOfResidence')}</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            id="placeOfResidence"
                                            name="placeOfResidence"
                                            value={formData.placeOfResidence}
                                            onChange={handleInputChange}
                                            className={formStyles.inputDark}
                                            placeholder={t('profile.enterPlaceOfResidence')}
                                        />
                                    ) : (
                                        <div className={formStyles.value}>{formData.placeOfResidence || t('profile.notProvided')}</div>
                                    )}
                                </div>
                            )}

                            <div className={formStyles.formGroup}>
                                <label htmlFor="language" className={formStyles.labelLight}>{t('profile.language')}</label>
                                <select
                                    id="language"
                                    name="language"
                                    value={user.preferredLanguage || 'en'}
                                    onChange={handleLanguageChange}
                                    className={formStyles.inputDark}
                                >
                                    <option value="en">{t('languages.english')}</option>
                                    <option value="sr">{t('languages.serbian')}</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            {isEditing ? (
                                <>
                                    <button className={styles.saveButton} onClick={handleSave}>
                                        {t('profile.saveChanges')}
                                    </button>
                                    <button className={styles.cancelButton} onClick={handleCancel}>
                                        {t('common.cancel')}
                                    </button>
                                </>
                            ) : (
                                <button className={styles.editButton} onClick={() => setIsEditing(true)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                    {t('profile.editProfile')}
                                </button>
                            )}
                        </div>
                        </>
                        )}

                        {/* Ratings Tab Content */}
{activeTab === 'ratings' && user && user._id && (
                    <div className={layoutStyles.content}>
                        <RatingDisplay 
                            userId={user._id} 
                                    isOwnProfile={true} 
                                />
                            </div>
                        )}

                        {/* Experience Tab Content - Only show for staff */}
                        {activeTab === 'experience' && user && user.userType !== 'employer' && (
                            <div className={layoutStyles.content}>
                                <div className={styles.cvSection}>
                                <div className={styles.cvHeader}>
                                    <h3 className={styles.cvTitle}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 7h-9"/>
                                            <path d="M14 17H5"/>
                                            <circle cx="17" cy="17" r="3"/>
                                            <circle cx="7" cy="7" r="3"/>
                                        </svg>
                                        {t('profile.previousExperience')}
                                    </h3>
                                    {!isAddingExperience && (
                                        <button className={styles.addExpButton} onClick={handleAddExperience}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                            </svg>
                                            {t('profile.addExperience')}
                                        </button>
                                    )}
                                </div>

                                {isAddingExperience && (
                                    <div className={styles.experienceForm}>
                                        <div className={formStyles.formGroup}>
                                            <label htmlFor="position" className={formStyles.label}>{t('profile.position')}</label>
                                            <input
                                                type="text"
                                                id="position"
                                                name="position"
                                                value={expFormData.position}
                                                onChange={handleExpInputChange}
                                                placeholder="e.g., Head Bartender"
                                                className={formStyles.input}
                                            />
                                        </div>

                                        <div className={formStyles.formGroup}>
                                            <label htmlFor="company" className={formStyles.label}>{t('profile.company')}</label>
                                            <input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={expFormData.company}
                                                onChange={handleExpInputChange}
                                                placeholder="e.g., The Golden Bar"
                                                className={formStyles.input}
                                            />
                                        </div>

                                        <div className={styles.monthYearInputs}>
                                            <div className={formStyles.formGroup}>
                                                <label htmlFor="startDate" className={formStyles.label}>{t('profile.startDate')}</label>
                                                <input
                                                    type="month"
                                                    id="startDate"
                                                    name="startDate"
                                                    value={expFormData.startDate}
                                                    onChange={handleExpInputChange}
                                                    className={formStyles.input}
                                                />
                                            </div>

                                            <div className={formStyles.formGroup}>
                                                <label htmlFor="endDate" className={formStyles.label}>{t('profile.endDate')}</label>
                                                <input
                                                    type="month"
                                                    id="endDate"
                                                    name="endDate"
                                                    value={expFormData.endDate}
                                                    onChange={handleExpInputChange}
                                                    className={formStyles.input}
                                                />
                                            </div>
                                        </div>

                                        <div className={formStyles.formGroup}>
                                            <label htmlFor="description" className={formStyles.label}>{t('profile.description')}</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={expFormData.description}
                                                onChange={handleExpInputChange}
                                                placeholder={t('profile.describeRole')}
                                                className={formStyles.textarea}
                                                rows={4}
                                            />
                                        </div>

                                        <div className={styles.formActions}>
                                            <button className={styles.saveButton} onClick={handleSaveExperience}>
                                                {editingExpId ? t('profile.updateExperience') : t('profile.addExperienceBtn')}
                                            </button>
                                            <button className={styles.cancelButton} onClick={handleCancelExperience}>
                                                {t('common.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.experiencesList}>
                                    {experiences.length === 0 && !isAddingExperience ? (
                                        <div className={styles.noExperience}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                <line x1="16" y1="2" x2="16" y2="6"/>
                                                <line x1="8" y1="2" x2="8" y2="6"/>
                                                <line x1="3" y1="10" x2="21" y2="10"/>
                                            </svg>
                                            <p>{t('profile.noExperience')}</p>
                                            <p className={styles.hint}>{t('profile.buildCV')}</p>
                                        </div>
                                    ) : (
                                        experiences.map(exp => (
                                            <div key={exp.id} className={styles.experienceItem}>
                                                <div className={styles.experienceHeader}>
                                                    <div className={styles.experienceMainInfo}>
                                                        <h4 className={styles.position}>{exp.position}</h4>
                                                        <p className={styles.company}>{exp.company}</p>
                                                    </div>
                                                    <div className={styles.experienceActions}>
                                                        <button 
                                                            className={styles.editIcon} 
                                                            onClick={() => handleEditExperience(exp)}
                                                            title="Edit"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                            </svg>
                                                        </button>
                                                        <button 
                                                            className={styles.deleteIcon} 
                                                            onClick={() => handleDeleteExperience(exp.id)}
                                                            title="Delete"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="3 6 5 6 21 6"/>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className={styles.date}>
                                                    {new Date(exp.startDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {' '}
                                                    {new Date(exp.endDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                </p>
                                                <p className={styles.description}>{exp.description}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default Profile;
